/**
 * Modal pour imprimer des étiquettes texte libre
 * Permet d'imprimer rapidement du texte sans produit ni template
 */

import { useState, useMemo, useEffect } from 'react';
import { labelTemplatesApi } from '../../api/label-templates.api';
import { getHardwareBridgeStatus, hardwareBridgeZebra, zebraAbsenteIci } from '../../services/printers/HardwareBridgeAdapter';

interface Props {
  onClose: () => void;
}

type LabelFormat = 'SHELF' | 'MEDIUM' | 'BULK';
type TextAlign = 'left' | 'center' | 'right';

type StatusMessage = {
  type: 'success' | 'error' | 'info';
  text: string;
} | null;

const FORMAT_LABELS: Record<LabelFormat, string> = {
  SHELF: 'Petit (50x25mm)',
  MEDIUM: 'Moyen (60x49mm)',
  BULK: 'Grand (100×150mm)',
};

const FONT_SIZES = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32];

const FORMATS: Record<string, { width: number; height: number }> = {
  SHELF: { width: 50, height: 25 },
  MEDIUM: { width: 60, height: 49 },
  BULK: { width: 100, height: 150 },
};

const STORAGE_KEY = 'freetext-label-last';

interface SavedLabel {
  text: string;
  format: LabelFormat;
  fontSize: number;
  align: TextAlign;
  bold: boolean;
  quantity: number;
}

function loadLastLabel(): SavedLabel | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveLastLabel(label: SavedLabel) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(label));
  } catch { /* ignore */ }
}

/** Génère le ZPL côté client (miroir de la logique backend) pour l'aperçu live */
function generatePreviewZpl(text: string, format: LabelFormat, fontSize: number, align: TextAlign, bold: boolean): string {
  const labelFormat = FORMATS[format] || FORMATS.SHELF;
  const dpi = 203;
  const mmToDots = (mm: number) => Math.round((mm * dpi) / 25.4);
  const widthDots = mmToDots(labelFormat.width);
  const heightDots = mmToDots(labelFormat.height);
  const fontHeight = Math.max(15, Math.round((fontSize * dpi) / 72));
  const fontWidth = bold ? Math.round(fontHeight * 0.6 * 1.1) : Math.round(fontHeight * 0.6);
  const alignMap: Record<string, string> = { left: 'L', center: 'C', right: 'R' };
  const alignCode = alignMap[align] || 'C';
  const escapeZPL = (s: string) => s.replace(/\^/g, '^5E').replace(/~/g, '^7E');
  const lineHeight = fontHeight + 4;
  const margin = mmToDots(3);
  const usableHeight = heightDots - margin * 2;
  const maxLines = Math.max(1, Math.floor(usableHeight / lineHeight));
  const lines = text.split('\n');
  const parts: string[] = ['^XA', '^CI28', '^MMT', '~SD25', `^PW${widthDots}`, `^LL${heightDots}`, '^LH0,0', '^PON'];
  const fieldWidth = widthDots - margin * 2;
  let y = margin;
  const totalTextHeight = Math.min(lines.length, maxLines) * lineHeight;
  if (totalTextHeight < usableHeight) {
    y = Math.round((heightDots - totalTextHeight) / 2);
  }
  for (let i = 0; i < Math.min(lines.length, maxLines); i++) {
    const escaped = escapeZPL(lines[i].trim());
    if (!escaped) { y += lineHeight; continue; }
    parts.push(`^FO${margin},${y}`);
    parts.push(`^A0N,${fontHeight},${fontWidth}`);
    parts.push(`^FB${fieldWidth},1,0,${alignCode},0`);
    parts.push(`^FD${escaped}^FS`);
    y += lineHeight;
  }
  parts.push('^XZ');
  return parts.join('\n');
}

function buildLabelaryUrl(format: LabelFormat, zpl: string): string {
  const labelFormat = FORMATS[format] || FORMATS.SHELF;
  const widthInches = (labelFormat.width / 25.4).toFixed(2);
  const heightInches = (labelFormat.height / 25.4).toFixed(2);
  return `http://api.labelary.com/v1/printers/8dpmm/labels/${widthInches}x${heightInches}/0/${encodeURIComponent(zpl)}`;
}

export function FreetextLabelModal({ onClose }: Props) {
  const saved = useMemo(() => loadLastLabel(), []);
  const [text, setText] = useState(saved?.text ?? '');
  const [format, setFormat] = useState<LabelFormat>(saved?.format ?? 'SHELF');
  const [fontSize, setFontSize] = useState(saved?.fontSize ?? 14);
  const [align, setAlign] = useState<TextAlign>(saved?.align ?? 'center');
  const [bold, setBold] = useState(saved?.bold ?? false);
  const [quantity, setQuantity] = useState(saved?.quantity ?? 1);
  const [printing, setPrinting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<StatusMessage>(null);
  const [showSavedBadge, setShowSavedBadge] = useState(!!saved);

  // Aperçu live via Labelary
  const previewUrl = useMemo(() => {
    if (!text.trim()) return null;
    const zpl = generatePreviewZpl(text, format, fontSize, align, bold);
    return buildLabelaryUrl(format, zpl);
  }, [text, format, fontSize, align, bold]);

  // Masquer le badge "restauré" après 3s
  useEffect(() => {
    if (showSavedBadge) {
      const t = setTimeout(() => setShowSavedBadge(false), 3000);
      return () => clearTimeout(t);
    }
  }, [showSavedBadge]);

  const handlePrint = async () => {
    if (!text.trim()) {
      setStatusMessage({ type: 'error', text: 'Veuillez saisir du texte' });
      return;
    }

    try {
      setPrinting(true);
      setStatusMessage({ type: 'info', text: 'Impression en cours...' });

      // Vérifier d'abord le Hardware Bridge local
      const bridgeStatus = await getHardwareBridgeStatus();
      const useLocalBridge = bridgeStatus?.hardware.zebra.available ?? false;
      const absente = zebraAbsenteIci(bridgeStatus);
      if (absente) throw new Error(absente);

      // Appeler l'API backend pour générer le ZPL
      const result = await labelTemplatesApi.printFreetext({
        text: text.trim(),
        format,
        fontSize,
        align,
        bold,
        quantity: useLocalBridge ? 1 : quantity,
      });

      if (useLocalBridge && result.zpl) {
        // Imprimer via le Hardware Bridge local
        for (let i = 0; i < quantity; i++) {
          const printResult = await hardwareBridgeZebra.printZPL(result.zpl);
          if (!printResult.success) {
            throw new Error(printResult.message);
          }
        }
        setStatusMessage({ type: 'success', text: `${quantity} étiquette(s) imprimée(s) via Hardware Bridge` });
      } else if (result.success) {
        setStatusMessage({ type: 'success', text: result.message });
      } else {
        setStatusMessage({ type: 'error', text: result.message });
        return;
      }

      // Sauvegarder la dernière impression
      saveLastLabel({ text: text.trim(), format, fontSize, align, bold, quantity });
    } catch (error: any) {
      console.error('Erreur impression texte libre:', error);
      setStatusMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Erreur lors de l\'impression',
      });
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500/75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Texte libre</h2>
              <p className="mt-1 text-sm text-gray-500">
                Imprimez une étiquette avec du texte personnalisé
              </p>
              {showSavedBadge && (
                <p className="mt-1 text-xs text-indigo-600">Dernière impression restaurée</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>

          <div className="space-y-4">
            {/* Texte */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Texte *
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Saisissez le texte de l'étiquette..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              />
              <p className="mt-1 text-xs text-gray-500">
                Utilisez Entrée pour les retours à la ligne
              </p>
            </div>

            {/* Format */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Format
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(Object.keys(FORMAT_LABELS) as LabelFormat[]).map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFormat(f)}
                    className={`px-3 py-2 rounded-md border text-sm font-medium ${
                      format === f
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {FORMAT_LABELS[f]}
                  </button>
                ))}
              </div>
            </div>

            {/* Taille police + Gras */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Taille police
                </label>
                <select
                  value={fontSize}
                  onChange={(e) => setFontSize(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {FONT_SIZES.map((s) => (
                    <option key={s} value={s}>{s}pt</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={bold}
                    onChange={(e) => setBold(e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Gras</span>
                </label>
              </div>
            </div>

            {/* Alignement */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alignement
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setAlign('left')}
                  className={`px-3 py-2 rounded-md border text-sm ${
                    align === 'left'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Gauche
                </button>
                <button
                  type="button"
                  onClick={() => setAlign('center')}
                  className={`px-3 py-2 rounded-md border text-sm ${
                    align === 'center'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Centre
                </button>
                <button
                  type="button"
                  onClick={() => setAlign('right')}
                  className={`px-3 py-2 rounded-md border text-sm ${
                    align === 'right'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Droite
                </button>
              </div>
            </div>

            {/* Quantité */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantité
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  className="w-12 h-12 flex items-center justify-center rounded-lg border-2 border-gray-300 text-xl font-bold text-gray-700 bg-white hover:bg-gray-100 active:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  −
                </button>
                <span className="min-w-[3rem] text-center text-2xl font-semibold text-gray-900">
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.min(100, q + 1))}
                  disabled={quantity >= 100}
                  className="w-12 h-12 flex items-center justify-center rounded-lg border-2 border-gray-300 text-xl font-bold text-gray-700 bg-white hover:bg-gray-100 active:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>
            </div>

            {/* Preview */}
            {previewUrl && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Aperçu
                </label>
                <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                  <img src={previewUrl} alt="Aperçu" className="max-w-full h-auto mx-auto" />
                </div>
              </div>
            )}

            {/* Status */}
            {statusMessage && (
              <div className={`px-4 py-3 rounded-md text-sm ${
                statusMessage.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : statusMessage.type === 'error'
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : 'bg-blue-50 border border-blue-200 text-blue-800'
              }`}>
                {statusMessage.text}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              disabled={printing}
            >
              Annuler
            </button>
            <button
              onClick={handlePrint}
              disabled={printing || !text.trim()}
              className="flex-1 px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {printing ? 'Impression...' : `Imprimer${quantity > 1 ? ` (${quantity})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
